import { useState, type ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import KoobLoaderText from './KoobLoaderText';
import KoobSpinner from './KoobSpinner';

type KoobLoaderFullscreenProps = {
  title?: ReactNode;
  description?: ReactNode;
  opacity?: 'full' | 'semi' | 'none';
  /** Shown instead of the Koob logo (white-labelled screens). */
  logoUrl?: string;
  logoAlt?: string;
  /** No logo at all: neither the Koob one nor a custom one. */
  hideLogo?: boolean;
};

export default function KoobLoaderFullscreen({
  title,
  description,
  opacity = 'full',
  logoUrl,
  logoAlt,
  hideLogo = false,
}: KoobLoaderFullscreenProps) {
  const [logoFailed, setLogoFailed] = useState(false);

  const overlay = cva(['fixed top-0 left-0 h-screen w-screen z-overlay'], {
    variants: {
      opacity: {
        full: 'bg-white',
        semi: 'bg-white bg-opacity-50 backdrop-blur',
        none: 'bg-white bg-opacity-0',
      },
    },
  });

  // A custom logo that fails to load falls back to no logo, never to Koob's:
  // a white-labelled screen must not leak the Koob brand.
  const showCustomLogo = !hideLogo && !!logoUrl && !logoFailed;
  const showKoobLogo = !hideLogo && !logoUrl;

  return (
    <div className={overlay({ opacity })}>
      <div className="h-full flex flex-col justify-center items-center">
        <div className="flex-col space-y-5 justify-center">
          {showCustomLogo && (
            <div className="flex justify-center">
              <img
                src={logoUrl}
                alt={logoAlt ?? ''}
                onError={() => setLogoFailed(true)}
                className="h-20 w-auto max-w-[240px] object-contain"
              />
            </div>
          )}

          {showKoobLogo && (
            <div className="flex justify-center">
              <img
                src="/logo512.png"
                alt="Koob"
                className="h-40 w-auto -my-10"
              />
            </div>
          )}

          <div className="flex justify-center">
            <KoobSpinner size={32} />
          </div>

          <div className="max-w-sm mx-auto">
            <KoobLoaderText title={title} description={description} />
          </div>
        </div>
      </div>
    </div>
  );
}
